import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { getAlerts, markAlertRead, snoozeAlert, markAllAlertsRead } from '../services/api';
import { toast } from 'sonner';
import {
  AlertTriangle, FileQuestion, GitMerge, Copy, TrendingUp, Bell,
  CheckCheck, Clock, ArrowRight, Inbox,
} from 'lucide-react';

const SOURCE_FILTERS = [
  { key: null, label: 'All' },
  { key: 'exception', label: 'Exceptions' },
  { key: 'recon', label: 'Recon' },
  { key: 'missing_doc', label: 'Missing Docs' },
  { key: 'duplicate', label: 'Duplicates' },
  { key: 'budget', label: 'Budget' },
  { key: 'unusual_spend', label: 'Unusual' },
];

const SOURCE_ICON = {
  exception: AlertTriangle,
  recon: GitMerge,
  missing_doc: FileQuestion,
  duplicate: Copy,
  budget: TrendingUp,
  unusual_spend: Bell,
};

const SEVERITY_STYLE = {
  high: { bg: 'rgba(217,138,74,0.12)', dot: '#D98A4A', text: '#D98A4A', label: 'High' },
  medium: { bg: 'rgba(45,122,93,0.10)', dot: '#2D7A5D', text: '#2D7A5D', label: 'Medium' },
  low: { bg: 'rgba(75,85,99,0.10)', dot: '#4B5563', text: '#4B5563', label: 'Low' },
};

const STATUS_TABS = [
  { key: 'active', label: 'Active' },
  { key: 'read', label: 'Read' },
  { key: 'all', label: 'All' },
];

export default function AlertsPage() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState(null);
  const [status, setStatus] = useState('active');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status };
      if (source) params.source = source;
      const res = await getAlerts(params);
      setAlerts(res.data.alerts || []);
      setCounts(res.data.source_counts || {});
    } catch (e) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [source, status]);

  useEffect(() => { load(); }, [load]);

  const handleRead = async (alert) => {
    try {
      await markAlertRead(alert.alert_id);
      setAlerts((prev) => prev.filter((a) => a.alert_id !== alert.alert_id));
      toast.success('Marked as read');
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const handleSnooze = async (alert) => {
    try {
      await snoozeAlert(alert.alert_id);
      setAlerts((prev) => prev.filter((a) => a.alert_id !== alert.alert_id));
      toast.success('Snoozed for 24 hours');
    } catch (e) {
      toast.error('Failed to snooze');
    }
  };

  const handleAction = async (alert) => {
    await handleRead(alert);
    navigate(alert.route);
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllAlertsRead();
      toast.success('All alerts marked as read');
      load();
    } catch (e) {
      toast.error('Failed to mark all read');
    }
  };

  const totalActive = Object.values(counts).reduce((sum, n) => sum + n, 0);

  return (
    <Layout title="Alerts Center">
      <div data-testid="alerts-page" className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold mb-2" style={{ color: '#D98A4A' }}>
              Real-Time Anomaly Detection
            </p>
            <h2 className="font-heading text-[28px] font-semibold tracking-tight" style={{ color: '#0A3622' }}>
              {totalActive > 0
                ? <>You have <span style={{ color: '#D98A4A' }}>{totalActive}</span> alert{totalActive === 1 ? '' : 's'} to review.</>
                : <>Nothing needs attention right now.</>}
            </h2>
            <p className="text-[14px] mt-1.5" style={{ color: '#4B5563' }}>
              Aggregated from exceptions, recon, missing docs, duplicates, budget, and unusual spending.
            </p>
          </div>
          {totalActive > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[12.5px] font-body font-semibold whitespace-nowrap"
              style={{ background: '#0A3622', color: '#FFFFFF' }}
              data-testid="mark-all-read-btn"
            >
              <CheckCheck size={14} /> Mark all read
            </button>
          )}
        </header>

        {/* Filter rail */}
        <div className="flex flex-col gap-3" data-testid="alerts-filters">
          <div className="flex flex-wrap items-center gap-2">
            {SOURCE_FILTERS.map((f) => {
              const isActive = source === f.key;
              const cnt = f.key ? counts[f.key] || 0 : totalActive;
              return (
                <button
                  key={f.label}
                  onClick={() => setSource(f.key)}
                  className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-[12.5px] font-body font-medium transition-all"
                  style={{
                    background: isActive ? '#0A3622' : '#FFFFFF',
                    color: isActive ? '#FFFFFF' : '#1A1A1A',
                    border: `1px solid ${isActive ? '#0A3622' : '#E5E3DB'}`,
                  }}
                  data-testid={`filter-${f.key || 'all'}`}
                >
                  {f.label}
                  <span
                    className="text-[10px] font-mono px-1.5 py-0.5 rounded-full"
                    style={{
                      background: isActive ? 'rgba(255,255,255,0.18)' : '#F7F6F2',
                      color: isActive ? '#FFFFFF' : '#4B5563',
                    }}
                  >
                    {cnt}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-1 p-1 rounded-full self-start" style={{ background: '#F7F6F2', border: '1px solid #E5E3DB' }}>
            {STATUS_TABS.map((t) => {
              const isActive = status === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setStatus(t.key)}
                  className="px-3.5 py-1.5 rounded-full text-[12px] font-body font-medium transition-colors"
                  style={{
                    background: isActive ? '#FFFFFF' : 'transparent',
                    color: isActive ? '#0A3622' : '#4B5563',
                    boxShadow: isActive ? '0 1px 3px rgba(10,54,34,0.08)' : 'none',
                  }}
                  data-testid={`status-tab-${t.key}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: '#0A3622', borderTopColor: 'transparent' }} />
          </div>
        ) : alerts.length === 0 ? (
          <EmptyState status={status} />
        ) : (
          <div className="space-y-2.5" data-testid="alerts-list">
            {alerts.map((a) => (
              <AlertRow
                key={a.alert_id}
                alert={a}
                onRead={() => handleRead(a)}
                onSnooze={() => handleSnooze(a)}
                onAction={() => handleAction(a)}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function AlertRow({ alert, onRead, onSnooze, onAction }) {
  const Icon = SOURCE_ICON[alert.source] || Bell;
  const sev = SEVERITY_STYLE[alert.severity] || SEVERITY_STYLE.low;

  return (
    <article
      className="group flex items-start gap-4 p-5 rounded-xl transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
      style={{
        background: '#FFFFFF',
        border: '1px solid #E5E3DB',
        opacity: alert.read ? 0.6 : 1,
      }}
      data-testid={`alert-row-${alert.alert_id}`}
    >
      {/* Severity dot + icon */}
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 relative"
        style={{ background: sev.bg }}
      >
        <Icon size={20} style={{ color: sev.dot }} strokeWidth={2} />
        {!alert.read && (
          <span
            className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full"
            style={{ background: sev.dot, boxShadow: '0 0 0 2px #FFFFFF' }}
          />
        )}
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span
            className="text-[10px] uppercase tracking-[0.18em] font-semibold"
            style={{ color: sev.text }}
          >
            {alert.source_label}
          </span>
          <span
            className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-full"
            style={{ background: sev.bg, color: sev.text }}
          >
            {sev.label}
          </span>
          {alert.amount != null && (
            <span className="text-[11px] font-mono" style={{ color: '#4B5563' }}>
              ${Math.abs(alert.amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          )}
        </div>
        <p className="font-heading text-[15px] font-semibold tracking-tight mb-0.5" style={{ color: '#0A3622' }} data-testid={`alert-title-${alert.alert_id}`}>
          {alert.title}
        </p>
        <p className="text-[13px] leading-relaxed" style={{ color: '#4B5563' }}>{alert.detail}</p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 flex-shrink-0 sm:opacity-0 group-hover:opacity-100 transition-opacity">
        {!alert.read && (
          <>
            <button
              onClick={onSnooze}
              title="Snooze 24 hours"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full text-[#4B5563] hover:bg-[#F7F6F2] transition-colors"
              data-testid={`snooze-${alert.alert_id}`}
            >
              <Clock size={15} />
            </button>
            <button
              onClick={onRead}
              title="Mark read"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full text-[#4B5563] hover:bg-[#F7F6F2] transition-colors"
              data-testid={`read-${alert.alert_id}`}
            >
              <CheckCheck size={15} />
            </button>
          </>
        )}
        <button
          onClick={onAction}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[12px] font-body font-semibold transition-all"
          style={{ background: '#D98A4A', color: '#FFFFFF' }}
          data-testid={`action-${alert.alert_id}`}
        >
          Take action <ArrowRight size={12} />
        </button>
      </div>
    </article>
  );
}

function EmptyState({ status }) {
  const isReadTab = status === 'read';
  return (
    <div
      className="flex flex-col items-center justify-center py-16 px-6 text-center rounded-2xl"
      style={{ background: '#FFFFFF', border: '1px dashed #E5E3DB' }}
      data-testid="alerts-empty"
    >
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ background: 'rgba(45,122,93,0.10)' }}>
        <Inbox size={26} style={{ color: '#2D7A5D' }} strokeWidth={1.7} />
      </div>
      <h3 className="font-heading text-[18px] font-semibold mb-1.5" style={{ color: '#0A3622' }}>
        {isReadTab ? 'No read alerts yet' : 'All clear.'}
      </h3>
      <p className="text-[13.5px] max-w-md leading-relaxed" style={{ color: '#4B5563' }}>
        {isReadTab
          ? 'Alerts you mark as read will appear here.'
          : 'Nothing needs attention right now. SnuggleMind is watching for exceptions, recon issues, missing docs, duplicates, and unusual spending.'}
      </p>
      {!isReadTab && (
        <Link
          to="/dashboard"
          className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-body font-semibold"
          style={{ color: '#D98A4A' }}
        >
          Back to dashboard <ArrowRight size={13} />
        </Link>
      )}
    </div>
  );
}
